<template>
  <div class="min-h-screen relative">
    <!-- Background Image -->
    <div class="absolute inset-0 -z-10">
      <img
        src="@/assets/ChooseTable/ChooseTableBackground.jpg"
        alt="Choose Table Background"
        class="w-full h-full object-cover object-center"
        @error="handleImageError"
        @load="handleImageLoad"
      />
      <div class="absolute inset-0 bg-black/80"></div>
    </div>

    <!-- Main Content -->
    <main class="pt-32 pb-20 px-4">
      <div class="max-w-6xl mx-auto">
        <!-- Title -->
        <div class="text-center mb-8">
          <h1
            class="text-white text-4xl font-semibold md:text-5xl mb-4 mt-10"
            style="font-family: 'DM Sans', sans-serif"
          >
            {{ t("chooseTable.title") }}
          </h1>
          <p
            class="text-white text-lg md:text-xl max-w-3xl mx-auto leading-relaxed"
          >
            {{ t("chooseTable.subtitle") }}
          </p>
        </div>

        <!-- Round Table Message -->
        <div v-if="showRoundTableMessage" class="text-center mb-6">
          <div
            class="bg-yellow-600 text-white px-6 py-3 rounded-lg inline-block"
          >
            <p class="font-semibold">{{ tableValidationMessage }}</p>
          </div>
        </div>

        <!-- Simple Seating Chart for Testing -->
        <div class="relative max-w-4xl mx-auto">
          <div class="p-4 sm:p-6 md:p-8 rounded-lg shadow-2xl bg-opacity-50">
            <!-- Restaurant Floor Plan -->
            <div
              class="relative min-h-[300px] sm:min-h-[400px] md:min-h-[500px] rounded-lg border-4 border-gray-400"
            >
              <div v-for="(table, index) in squareTables" :key="table">
                <div :class="getTablePosition(index)" class="absolute">
                  <div
                    @click="
                      !isReserved(table)  &&
                        selectTable(table)
                    "
                    :class="[
                      'w-12 h-12 sm:w-16 sm:h-16 md:w-20 md:h-20 transition-all duration-300 flex items-center justify-center text-white font-bold border-2 text-xs sm:text-sm md:text-base',
                      isReserved(table)
                        ? 'cursor-not-allowed bg-gray-500 bg-opacity-60 border-gray-500 opacity-60'
                        : isCurrentReservationTable(table)
                        ? 'cursor-not-allowed bg-[#e6ac29] ring-4 ring-white opacity-90'
                        : 'cursor-pointer hover:scale-110 border-[#F5BE32] bg-[#F5BE32] bg-opacity-20 hover:bg-opacity-30',
                      selectedTable === table
                        ? 'bg-[#e6ac29] ring-4 ring-white'
                        : '',
                    ]"
                  >
                    T{{ index + 1 }}
                  </div>
                </div>
              </div>

              <!-- <div class="absolute top-32 right-8">
                <div class="bg-gray-500 w-20 h-20 rounded-lg opacity-60 flex items-center justify-center border-2 border-gray-500 bg-gray-500 bg-opacity-20">
                  <span class="text-white text-xs font-bold">RESERVED</span>
                </div>
              </div> -->

              <!-- Round Tables in Center -->
              <div v-for="(table, index) in roundTables" :key="table">
                <div :class="getRoundTablePosition(index)" class="absolute">
                  <div
                    @click="!isReserved(table) && selectTable(table)"
                    :class="[
                      'w-12 h-12 sm:w-16 sm:h-16 md:w-20 md:h-20 transition-all duration-300 flex items-center justify-center text-white font-bold border-2 text-xs sm:text-sm md:text-base',
                      isReserved(table)
                        ? 'rounded-full cursor-not-allowed bg-gray-500 bg-opacity-60 border-gray-500 opacity-60'
                        : 'rounded-full cursor-pointer hover:scale-110 border-[#F5BE32] bg-[#F5BE32] bg-opacity-20 hover:bg-[#F5BE32] hover:bg-opacity-30',
                      selectedTable === table
                        ? 'bg-[#e6ac29] ring-4 ring-white'
                        : '',
                    ]"
                  >
                    T{{ index + 13 }}
                  </div>
                </div>
              </div>

              <!-- Booths -->
              <!-- <div class="absolute top-4 left-4">
                <div 
                  @click="selectTable('booth1')"
                  :class="[
                    'w-16 h-12 rounded-lg cursor-pointer transition-all duration-300 hover:scale-110 flex items-center justify-center text-black font-bold text-sm border-2 border-red-500 bg-red-500 bg-opacity-20',
                    selectedTable === 'booth1' ? 'bg-[#e6ac29] ring-2 ring-white' : 'bg-transparent hover:bg-[#F5BE32] hover:bg-opacity-30'
                  ]"
                >
                  B1
                </div>
              </div> -->

              <!-- <div class="absolute top-4 right-4">
                <div 
                  @click="selectTable('booth4')"
                  :class="[
                    'w-16 h-12 rounded-lg cursor-pointer transition-all duration-300 hover:scale-110 flex items-center justify-center text-black font-bold text-sm border-2 border-red-500 bg-red-500 bg-opacity-20',
                    selectedTable === 'booth4' ? 'bg-[#e6ac29] ring-2 ring-white' : 'bg-transparent hover:bg-[#F5BE32] hover:bg-opacity-30'
                  ]"
                >
                  B4
                </div>
              </div> -->
            </div>
          </div>
        </div>

        <!-- Continue Button -->
        <div class="text-center mt-12">
          <button
            @click="continueToNextStep"
            :disabled="!selectedTable"
            :class="[
              'px-8 py-4 text-xl font-[\'Cormorant_SC\'] font-bold transition-all duration-300 mb-10',
              selectedTable
                ? 'bg-[#F5BE32] text-black hover:bg-[#e6ac29] shadow-[0_0_20px_#F5BE32]'
                : 'bg-gray-600 text-gray-400 cursor-not-allowed',
            ]"
          >
            {{ t("chooseTable.continue") }}
          </button>
        </div>

        <!-- Progress Indicators -->
        <div class="flex justify-center items-center mt-2 gap-3">
          <span class="w-2 h-2 rounded-full bg-gray-400 block"></span>
          <span class="w-2 h-2 rounded-full bg-gray-400 block"></span>
          <span class="w-2 h-2 rounded-full bg-[#F5BE32] block"></span>
          <span class="w-2 h-2 rounded-full bg-gray-400 block"></span>
        </div>

        <!-- Selection Info -->
        <div v-if="selectedTable" class="text-center mt-4">
          <p class="text-[#F5BE32] text-lg font-semibold">
            {{
              t("chooseTable.selected", {
                tableName: getTableDisplayName(selectedTable),
              })
            }}
          </p>
        </div>
      </div>
    </main>
  </div>
</template>

<script setup>
import { ref } from "vue";
import { useRouter } from "vue-router";
import { useI18n } from "vue-i18n";

const { t } = useI18n();

const router = useRouter();
const selectedTable = ref(null);
const reservedTables = ref([]);
const showRoundTableMessage = ref(false);
const tableValidationMessage = ref("");

// Initialize selected table from localStorage, but allow reselection
selectedTable.value = localStorage.getItem("selectedTable");

const selectTable = (tableId) => {
  const tableNumber = parseInt(tableId.replace("table", ""));
  const isRoundTable = tableNumber >= 13;
  const guests = parseInt(localStorage.getItem("bookingGuests") || "2");

  // Validation: round table (max 2 guests)
  if (isRoundTable && guests > 2) {
    tableValidationMessage.value = t("chooseTable.roundTableMessage");
    showRoundTableMessage.value = true;
    setTimeout(() => (showRoundTableMessage.value = false), 3000);
    return;
  }

  // Validation: square table (max 4 guests)
  if (!isRoundTable && guests > 4) {
    tableValidationMessage.value = t("chooseTable.squareTableMessage");
    showRoundTableMessage.value = true;
    setTimeout(() => (showRoundTableMessage.value = false), 3000);
    return;
  }

  // ✅ Passed validation — now select the table
  selectedTable.value = tableId;

  localStorage.setItem("selectedTable", tableId);
  showRoundTableMessage.value = false;
};


const isReserved = (tableId) => {
  // A table is reserved if it's in the reserved tables list
  // We don't check against selectedTable because we want to allow reselection
  return reservedTables.value.includes(tableId);
};
const isCurrentReservationTable = (tableId) => {
  return tableId === selectedTable.value;
};
const squareTables = [
  "table1",
  "table2",
  "table3",
  "table4",
  "table5",
  "table6",
  "table7",
  "table8",
  "table9",
  "table10",
  "table11",
  "table12",
];
const roundTables = [
  "table13",
  "table14",
  "table15",
  "table16",
  "table17",
  "table18",
];

const getTablePosition = (index) => {
  // Position tables clockwise: bottom-left → top-left → top-right → bottom-right
  const positions = [
    // Bottom-left (table1-3)
    "bottom-2 left-2 sm:bottom-4 sm:left-4 md:bottom-4 md:left-4",
    "bottom-16 left-2 sm:bottom-28 sm:left-4 md:bottom-28 md:left-4",
    "bottom-32 left-2 sm:bottom-52 sm:left-4 md:bottom-52 md:left-4",

    // Top-left (table4-6)
    "top-2 left-2 sm:top-4 sm:left-4 md:top-4 md:left-4",
    "top-2 left-16 sm:top-4 sm:left-28 md:top-4 md:left-28",
    "top-2 left-32 sm:top-4 sm:left-52 md:top-4 md:left-52",

    // Top-right (table7-9)
    "top-2 right-32 sm:top-4 sm:right-52 md:top-4 md:right-52",
    "top-2 right-16 sm:top-4 sm:right-28 md:top-4 md:right-28",
    "top-2 right-2 sm:top-4 sm:right-4 md:top-4 md:right-4",

    // Bottom-right (table10-12)
    "bottom-32 right-2 sm:bottom-52 sm:right-4 md:bottom-52 md:right-4",
    "bottom-16 right-2 sm:bottom-28 sm:right-4 md:bottom-28 md:right-4",
    "bottom-2 right-2 sm:bottom-4 sm:right-4 md:bottom-4 md:right-4",
  ];

  return (
    positions[index] || "top-2 left-2 sm:top-4 sm:left-4 md:top-4 md:left-4"
  ); // fallback position
};

const getRoundTablePosition = (index) => {
  // Position round tables in center, 2 rows of 3 tables each
  const positions = [
    // Top row (table13-15)
    "top-1/2 left-1/3 transform -translate-x-1/2 -translate-y-1/2",
    "top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2",
    "top-1/2 left-2/3 transform -translate-x-1/2 -translate-y-1/2",

    // Bottom row (table16-18)
    "bottom-8 sm:bottom-16 left-1/3 transform -translate-x-1/2",
    "bottom-8 sm:bottom-16 left-1/2 transform -translate-x-1/2",
    "bottom-8 sm:bottom-16 left-2/3 transform -translate-x-1/2",
  ];

  return (
    positions[index] ||
    "top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2"
  ); // fallback position
};
const getTableDisplayName = (tableId) => {
  // Extract table number from tableId (e.g., 'table1' -> '1')
  const tableNumber = tableId.replace("table", "");

  // Determine if it's a square or round table
  const isRoundTable = parseInt(tableNumber) >= 13;
  const tableType = isRoundTable ? "round" : "square";

  // Return format: "T{number} ({translated type})"
  return `T${tableNumber} (${t(`chooseTable.tableTypes.${tableType}`)})`;
};

const handleImageError = (event) => {
  console.error("Background image failed to load:", event.target.src);
  // Fallback to a different background
  event.target.style.display = "none";
};

const handleImageLoad = () => {
  console.log("Background image loaded successfully");
};

import {
  getFirestore,
  collection,
  query,
  where,
  getDocs,
  doc,
  updateDoc,
} from "firebase/firestore";
import { getAuth } from "firebase/auth";
import { onMounted } from "vue";

const db = getFirestore();
const auth = getAuth();

let reservationId = null;
const selectedDuration = localStorage.getItem("duration");
const selectedDate = localStorage.getItem("date");

onMounted(async () => {
  try {
    const user = auth.currentUser;
    if (!user) {
      router.push("/login");
      return;
    }

    if (!selectedDuration || !selectedDate) {
      console.error("Missing duration or date in localStorage");
      router.push("/bookingduration");
      return;
    }

    // Fetch user's reservation
    reservationId = localStorage.getItem("reservationId");

    if (!reservationId) {
      // fallback: find the latest reservation (optional)
      const userQuery = query(
        collection(db, "reservations"),
        where("userId", "==", user.uid),
        where("duration", "==", selectedDuration)
      );
      const userSnapshot = await getDocs(userQuery);
      if (!userSnapshot.empty) {
        const docSnap = userSnapshot.docs[userSnapshot.docs.length - 1]; // or sort by createdAt
        reservationId = docSnap.id;
        localStorage.setItem("reservationId", reservationId); // keep it in sync
      } else {
        console.error("Reservation not found.");
        router.push("/bookingduration");
        return;
      }
    }

    // Fetch reserved tables
    const reservedQuery = query(
      collection(db, "reservations"),
      where("duration", "==", selectedDuration),
      where("date", "==", selectedDate),
      where("status", "in", ["pending", "confirmed"])
    );
    const reservedSnapshot = await getDocs(reservedQuery);
    
    // Filter out the current user's reservation so they can reselect their table
    reservedTables.value = reservedSnapshot.docs
      .filter((doc) => doc.id !== reservationId) // Exclude current user's reservation
      .map((doc) => doc.data().tableId)
      .filter(Boolean);

    console.log(
      "Total reservations found for this date and duration:",
      reservedSnapshot.size
    );
    reservedSnapshot.forEach((doc) => {
      console.log("Reservation:", doc.id, "Table:", doc.data().tableId);
    });
  } catch (error) {
    console.error("Error fetching data:", error);
    router.push("/login");
  }
});

const continueToNextStep = async () => {
  if (!selectedTable.value || !reservationId) {
    console.error("Missing table or reservation ID.");
    return;
  }

  try {
    const reservationRef = doc(db, "reservations", reservationId);
    const resSnap = await getDocs(
      query(
        collection(db, "reservations"),
        where("__name__", "==", reservationId)
      )
    );

    if (!resSnap.empty) {
      const oldData = resSnap.docs[0].data();
      const oldTableId = oldData.tableId;

      // Only update if it's a new table
      if (oldTableId !== selectedTable.value) {
        await updateDoc(reservationRef, {
          tableId: selectedTable.value,
          tableName: getTableDisplayName(selectedTable.value),
        });
      }
    } else {
      console.warn("Reservation document not found for update.");
    }

    // Update localStorage and go to next page
    localStorage.setItem("selectedTable", selectedTable.value);
    localStorage.setItem(
      "selectedTableName",
      getTableDisplayName(selectedTable.value)
    );
    router.push("/deposit");
  } catch (error) {
    console.error("Error updating reservation with table:", error);
  }
};

</script>

<style scoped>
/* Custom scrollbar */
::-webkit-scrollbar {
  width: 8px;
}

::-webkit-scrollbar-track {
  background: #1f1f1f;
}

::-webkit-scrollbar-thumb {
  background: #f5be32;
  border-radius: 4px;
}

::-webkit-scrollbar-thumb:hover {
  background: #e6ac29;
}
</style>
